import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SeedingIcon from "@mui/icons-material/Agriculture";

const UsernameWordsSection = () => {
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWord, setEditingWord] = useState(null);
  const [formData, setFormData] = useState({ word: "", word_type: "prefix" });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [generatedUsernames, setGeneratedUsernames] = useState([]);

  useEffect(() => {
    fetchWords();
  }, []);

  const fetchWords = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/username-words`
      );
      setWords(response.data.words);
    } catch (err) {
      console.error("Failed to fetch words:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (word = null) => {
    if (word) {
      setEditingWord(word);
      setFormData({ word: word.word, word_type: word.word_type });
    } else {
      setEditingWord(null);
      setFormData({ word: "", word_type: "prefix" });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingWord(null);
    setFormData({ word: "", word_type: "prefix" });
  };

  const handleSave = async () => {
    if (!formData.word.trim()) return;
    setSaving(true);

    try {
      if (editingWord) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/username-words/${editingWord.id}`,
          formData
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/username-words`,
          formData
        );
      }
      fetchWords();
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save word:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this word?")) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/username-words/${id}`
      );
      fetchWords();
    } catch (err) {
      console.error("Failed to delete word:", err);
    }
  };

  const handleToggleActive = async (word) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/username-words/${word.id}`,
        { is_active: !word.is_active }
      );
      fetchWords();
    } catch (err) {
      console.error("Failed to toggle word:", err);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("This will add default Malayalam words to the database. Existing words will be skipped. Continue?")) return;
    setSeeding(true);
    setSeedResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/username-words/seed`
      );
      setSeedResult(response.data);
      fetchWords();
    } catch (err) {
      console.error("Failed to seed words:", err);
      setSeedResult({ error: "Failed to seed words" });
    } finally {
      setSeeding(false);
    }
  };

  const filteredWords = words.filter((w) => {
    const matchesSearch = w.word.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || w.word_type === filterType;
    return matchesSearch && matchesType;
  });

  const prefixes = words.filter((w) => w.word_type === "prefix" && w.is_active);
  const suffixes = words.filter((w) => w.word_type === "suffix" && w.is_active);
  const standalone = words.filter((w) => w.word_type === "standalone" && w.is_active);

  // Generate random username using the same logic as the chatroom
  const generateRandomUsername = () => {
    if (prefixes.length === 0 && standalone.length === 0) {
      return `Guest${Math.floor(Math.random() * 9999)}`;
    }

    // 70% chance for prefix+suffix combo, 30% for standalone
    const useStandalone =
      standalone.length > 0 &&
      (prefixes.length === 0 || Math.random() < 0.3);

    let baseName;
    let components;

    if (useStandalone) {
      const word = standalone[Math.floor(Math.random() * standalone.length)];
      baseName = word.word;
      components = { type: "standalone", standalone: word.word };
    } else {
      const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
      const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
      baseName = prefix.word + suffix.word;
      components = { type: "combo", prefix: prefix.word, suffix: suffix.word };
    }

    // Add random 2-digit number (0-99)
    const randomNum = Math.floor(Math.random() * 100);
    return { username: `${baseName}${randomNum}`, components, number: randomNum };
  };

  const handleGenerateUsernames = () => {
    const newUsernames = [];
    for (let i = 0; i < 5; i++) {
      newUsernames.push(generateRandomUsername());
    }
    setGeneratedUsernames(newUsernames);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={seeding ? <CircularProgress size={16} sx={{ color: "#ff9800" }} /> : <SeedingIcon />}
            onClick={handleSeed}
            disabled={seeding}
            sx={{
              color: "#ff9800",
              borderColor: "#ff9800",
              "&:hover": { borderColor: "#ff9800", backgroundColor: "rgba(255, 152, 0, 0.1)" },
            }}
          >
            {seeding ? "Seeding..." : "Seed Defaults"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: "#541b14",
              "&:hover": { backgroundColor: "#6d231a" },
            }}
          >
            Add Word
          </Button>
        </Box>
      </Box>

      {/* Seed Result */}
      {seedResult && (
        <Paper sx={{ p: 1.5, mb: 2, backgroundColor: seedResult.error ? "#4a1515" : "#1b3a1b", borderRadius: 1, border: `1px solid ${seedResult.error ? "#ef5350" : "#4caf50"}` }}>
          <Typography variant="body2" sx={{ color: "#fff" }}>
            {seedResult.error ? seedResult.error : `Seeding complete: ${seedResult.inserted} inserted, ${seedResult.skipped} skipped (already exist)`}
          </Typography>
        </Paper>
      )}

      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#4caf50", fontWeight: 700 }}>{prefixes.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Prefixes</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#2196f3", fontWeight: 700 }}>{suffixes.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Suffixes</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#ff9800", fontWeight: 700 }}>{standalone.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Standalone</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#9c27b0", fontWeight: 700 }}>{prefixes.length * suffixes.length + standalone.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Possible Combos</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Search words..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#fff",
                  "& fieldset": { borderColor: "#394867" },
                  "&:hover fieldset": { borderColor: "#541b14" },
                  "&.Mui-focused fieldset": { borderColor: "#541b14" },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: "#9e9e9e" }}>Filter by Type</InputLabel>
              <Select
                value={filterType}
                label="Filter by Type"
                onChange={(e) => setFilterType(e.target.value)}
                sx={{
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                  "& .MuiSvgIcon-root": { color: "#9e9e9e" },
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="prefix">Prefix</MenuItem>
                <MenuItem value="suffix">Suffix</MenuItem>
                <MenuItem value="standalone">Standalone</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Words Table */}
      <Paper sx={{ backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Word</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Type</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Active</TableCell>
                <TableCell align="center" sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredWords.map((word) => (
                <TableRow key={word.id} hover sx={{ "&:hover": { backgroundColor: "#1a1a2e" } }}>
                  <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867", py: 0.75 }}>
                    {word.word}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Chip
                      label={word.word_type}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.75rem",
                        backgroundColor:
                          word.word_type === "prefix" ? "#1b5e20" :
                          word.word_type === "suffix" ? "#0d47a1" : "#e65100",
                        color: "#fff",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Switch
                      size="small"
                      checked={word.is_active}
                      onChange={() => handleToggleActive(word)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <IconButton size="small" onClick={() => handleOpenDialog(word)} sx={{ color: "#2196f3", p: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(word.id)} sx={{ color: "#ef5350", p: 0.5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Random Username Generator */}
      <Paper sx={{ mt: 3, p: 3, backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 600 }}>
              Username Generator
            </Typography>
            <Typography variant="caption" sx={{ color: "#666" }}>
              Preview how usernames will be generated (70% prefix+suffix, 30% standalone, number 0-99)
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleGenerateUsernames}
            disabled={prefixes.length === 0 && standalone.length === 0}
            sx={{
              backgroundColor: "#541b14",
              "&:hover": { backgroundColor: "#6d231a" },
            }}
          >
            Generate 5 Usernames
          </Button>
        </Box>

        {generatedUsernames.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {generatedUsernames.map((item, index) => (
              <Paper
                key={index}
                sx={{
                  p: 1.5,
                  backgroundColor: "#1a1a2e",
                  borderRadius: 1,
                  border: "1px solid #394867",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography variant="h6" sx={{ color: "#ff9800", fontWeight: 600, fontFamily: "monospace" }}>
                    {item.username}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {item.components.type === "combo" ? (
                      <>
                        <Chip
                          label={item.components.prefix}
                          size="small"
                          sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#1b5e20", color: "#fff" }}
                        />
                        <Typography sx={{ color: "#666" }}>+</Typography>
                        <Chip
                          label={item.components.suffix}
                          size="small"
                          sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#0d47a1", color: "#fff" }}
                        />
                        <Typography sx={{ color: "#666" }}>+</Typography>
                        <Chip
                          label={item.number}
                          size="small"
                          sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#424242", color: "#fff" }}
                        />
                      </>
                    ) : (
                      <>
                        <Chip
                          label={item.components.standalone}
                          size="small"
                          sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#e65100", color: "#fff" }}
                        />
                        <Typography sx={{ color: "#666" }}>+</Typography>
                        <Chip
                          label={item.number}
                          size="small"
                          sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#424242", color: "#fff" }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
                <Chip
                  label={item.components.type === "combo" ? "PREFIX + SUFFIX" : "STANDALONE"}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.6rem",
                    backgroundColor: item.components.type === "combo" ? "rgba(156, 39, 176, 0.2)" : "rgba(230, 81, 0, 0.2)",
                    color: item.components.type === "combo" ? "#ce93d8" : "#ffb74d",
                    border: `1px solid ${item.components.type === "combo" ? "#9c27b0" : "#e65100"}`,
                  }}
                />
              </Paper>
            ))}
          </Box>
        )}

        {generatedUsernames.length === 0 && (
          <Box sx={{ textAlign: "center", py: 3, color: "#666" }}>
            <Typography variant="body2">
              Click "Generate 5 Usernames" to preview random username combinations
            </Typography>
          </Box>
        )}

        {prefixes.length === 0 && standalone.length === 0 && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography variant="body2" sx={{ color: "#ef5350" }}>
              No active words available. Add some prefix/suffix or standalone words first.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} PaperProps={{ sx: { backgroundColor: "#16213e", color: "#fff" } }}>
        <DialogTitle>{editingWord ? "Edit Word" : "Add New Word"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Word"
            fullWidth
            value={formData.word}
            onChange={(e) => setFormData({ ...formData, word: e.target.value })}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "#394867" },
                "&:hover fieldset": { borderColor: "#541b14" },
                "&.Mui-focused fieldset": { borderColor: "#541b14" },
              },
              "& .MuiInputLabel-root": { color: "#9e9e9e" },
            }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: "#9e9e9e" }}>Type</InputLabel>
            <Select
              value={formData.word_type}
              label="Type"
              onChange={(e) => setFormData({ ...formData, word_type: e.target.value })}
              sx={{
                color: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#394867" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#541b14" },
                "& .MuiSvgIcon-root": { color: "#9e9e9e" },
              }}
            >
              <MenuItem value="prefix">Prefix</MenuItem>
              <MenuItem value="suffix">Suffix</MenuItem>
              <MenuItem value="standalone">Standalone</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: "#9e9e9e" }}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !formData.word.trim()}
            sx={{ color: "#4caf50" }}
          >
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsernameWordsSection;
